import React, { useCallback, useContext, useMemo } from "react";
import _ from "lodash";
import * as yup from "yup";
import HeadingSection from "../UI/HeadingSection";
import { Form, Formik } from "formik";
import { Field, FormButtonCluster } from "../UI/forms";
import * as r from "../../requests/interactionsRequests";
import AlertContext from "../../context/alert/AlertContext";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import { useParams } from "react-router-dom";
import UserContext from "../../context/user/UserContext";

const validationSchema = yup.object().shape({
  topic: yup.string().required("Topic is required."),
  RefType: yup.string().required("Reference Type is required."),
  description: yup.string().required("Description is required."),
  stat: yup.string().required("Status is required."),
  interaction_type: yup.string().required("Type is required."),
});

const InteractionForm = ({
  data,
  count,
  interactionId,
  setInteractionId,
  currentInteraction,
  setShow,
  setData,
  interactionTopics = [],
  refetchQuery,
}) => {
  const { showSuccessAlert } = useContext(AlertContext);
  const { quoteId } = useParams();
  const { activeUser } = useContext(UserContext);

  const { quote } = useContext(ConfiguratorContext);
  const { erpOrderNo, sameAsBilling, shippingAddressId, billingAddressId } =
    quote || {};

  const { topic, RefType, description, stat, interaction_type } =
    currentInteraction || {};

  const initialValues = {
    topic: topic || 0,
    RefType: RefType || "C",
    description: description || "",
    stat: stat || "A",
    interaction_type: interaction_type || "C",
  };

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const { rows } = data;

        if (interactionId === "create") {
          const result = await r.createInteraction({
            input: {
              ...values,
              erpOrderNo,
              firstName: activeUser?.firstName,
              lastName: activeUser?.lastName,
              ref_num_id: quote?.customer?.erpId,
              quoteId: quote?.id,
              groupId: !quoteId ? activeUser?.groupId : undefined,
              addressId: sameAsBilling ? billingAddressId : shippingAddressId,
            },
          });
          const newRows = _.sortBy([...rows, result], ["createdAt"]);

          const newData = { rows: newRows, count: count + 1 };
          setInteractionId(result.interaction_id);

          showSuccessAlert(
            `Successfully created Interaction: ${result.interaction_id}`
          );
          setData(newData);
          setShow(false);
          refetchQuery();
        } else {
          const updatedRecord = await r.updateInteraction({
            id: interactionId,
            input: values,
          });

          showSuccessAlert(
            `Successfully updated Interaction: ${interactionId}`
          );

          const newRows = _.sortBy(
            rows.map((row) => {
              const { id: rowId } = row;
              if (rowId === interactionId) {
                return updatedRecord;
              }
              return row;
            }),
            ["createdAt"]
          );

          const newData = { ...data, rows: newRows };
          setData(newData);
          resetForm();
          setShow(false);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      data,
      interactionId,
      erpOrderNo,
      count,
      setInteractionId,
      showSuccessAlert,
      setData,
      setShow,
      quote,
      sameAsBilling,
      shippingAddressId,
      billingAddressId,
      refetchQuery,
      activeUser,
      quoteId,
    ]
  );

  const topicOptions = useMemo(
    () => [
      <option value="" key="default">
        Select a Topic
      </option>,
      ...interactionTopics.map(({ topic, ...rest }, i) => {
        console.log(rest);
        return (
          <option value={topic} key={i}>
            {topic}
          </option>
        );
      }),
    ],
    [interactionTopics]
  );
  const statusOptions = useMemo(
    () =>
      [
        { label: "Active", value: "A" },
        { label: "Complete", value: "C" },
      ].map(({ label, value }, i) => (
        <option value={value} key={`status_${value}`}>
          {label}
        </option>
      )),
    []
  );
  // const referenceOptions = useMemo(
  //   () =>
  //     [
  //       { label: "Customer Order", value: "C" },
  //       { label: "Item", value: "I" },
  //       { label: "Estimate", value: "E" },
  //       { label: "Project", value: "P" },
  //       { label: "Estimate Project", value: "R" },
  //     ].map(({ label, value }, i) => (
  //       <option value={value} key={`${value}_${i}`}>
  //         {label}
  //       </option>
  //     )),
  //   []
  // );
  // const interactionTypes = useMemo(
  //   () =>
  //     [
  //       { label: "Customer", value: "C" },
  //       { label: "Vendor", value: "V" },
  //       { label: "Prospect", value: "P" },
  //       { label: "Sales Contact", value: "S" },
  //     ].map(({ label, value }, i) => (
  //       <option value={value} key={`${value}_${i}`}>
  //         {label}
  //       </option>
  //     )),
  //   []
  // );

  const onDelete = useCallback(() => {
    r.deleteInteraction({ id: interactionId });
    const { rows } = data;
    const newRows = [...rows];
    _.remove(newRows, (row) => row.id === interactionId);
    setData({ rows: newRows, count: count - 1 });
    setShow(false);
  }, [interactionId, count, data, setData, setShow]);

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      validateOnMount={false}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {() => (
        <>
          <HeadingSection
            my="1rem"
            hideCreate
            hideBack
            backButtonLink={() => setShow(false)}
            title={`${
              interactionId === "create" ? "Create" : "View"
            } Interaction`}
            subtitle=""
          />
          <Form style={{ minWidth: "300px", width: "100%" }}>
            <Field required as="select" name="topic" labelText="Topic">
              {topicOptions}
            </Field>
            <Field
              required
              as="textarea"
              name="description"
              labelText="Description"
            />

            <Field required as="select" name="stat" labelText="Status">
              {statusOptions}
            </Field>
            {/* <Field
              required
              as="select"
              name="interaction_type"
              labelText="Type"
            >
              {interactionTypes}
            </Field>
            <Field
              required
              as="select"
              name="RefType"
              labelText="Reference Type"
            >
              {referenceOptions}
            </Field> */}

            <FormButtonCluster
              onDelete={onDelete}
              onCancel={() => setShow(false)}
              hideDelete={interactionId === "create"}
              recordAction={interactionId === "create" ? "create" : "edit"}
              deleteMessage={`Are you sure you want to delete this interation?`}
            />
          </Form>
        </>
      )}
    </Formik>
  );
};

export default InteractionForm;
